import { createContext, useContext, useState, useEffect, useCallback } from "react";

const ABTestContext = createContext();

// A/B Test Variants Configuration
const AB_TESTS = {
  cta_button: {
    variants: [
      { id: "A", color: "bg-navy-900 hover:bg-navy-800", text: "order" },
      { id: "B", color: "bg-marine-600 hover:bg-marine-700", text: "quote" },
      { id: "C", color: "bg-orange-500 hover:bg-orange-600", text: "consultation" }
    ],
    // Text translations for each variant
    texts: {
      order: { en: "Order Service", ru: "Заказать услугу", fr: "Commander", uk: "Замовити послугу" },
      quote: { en: "Get Free Quote", ru: "Получить цену", fr: "Devis gratuit", uk: "Отримати ціну" },
      consultation: { en: "Free Consultation", ru: "Бесплатная консультация", fr: "Consultation gratuite", uk: "Безкоштовна консультація" }
    }
  },
  hero_cta: {
    variants: [
      { id: "A", primary: "order", secondary: "services" },
      { id: "B", primary: "quote", secondary: "reviews" },
      { id: "C", primary: "consultation", secondary: "contact" }
    ]
  }
};

// Safe storage wrappers
const safeGet = (storage, key, fallback = null) => { try { return storage.getItem(key); } catch { return fallback; } };
const safeSet = (storage, key, val) => { try { storage.setItem(key, val); } catch {} };

// Get or assign variant for user
const getVariant = (testName) => {
  const storageKey = `ab_test_${testName}`;
  let variant = safeGet(localStorage, storageKey);
  
  if (!variant) {
    const test = AB_TESTS[testName];
    if (!test) return null;
    
    // Random assignment
    const randomIndex = Math.floor(Math.random() * test.variants.length);
    variant = test.variants[randomIndex].id;
    safeSet(localStorage, storageKey, variant);
  }
  
  return variant;
};

// Track A/B test event
const trackABEvent = async (testName, variantId, eventType, metadata = {}) => {
  const event = {
    test_name: testName,
    variant_id: variantId,
    event_type: eventType,
    metadata,
    timestamp: new Date().toISOString(),
    session_id: safeGet(sessionStorage, "session_id") || generateSessionId(),
    user_id: safeGet(localStorage, "user_id")
  };
  
  try {
    const events = JSON.parse(safeGet(localStorage, "ab_events") || "[]");
    events.push(event);
    safeSet(localStorage, "ab_events", JSON.stringify(events.slice(-100)));
  } catch {}
  
  try {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/ab-event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event)
    });
  } catch {}
};

const generateSessionId = () => {
  const id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  safeSet(sessionStorage, "session_id", id);
  return id;
};

export const ABTestProvider = ({ children }) => {
  const [variants, setVariants] = useState({});
  
  useEffect(() => {
    // Initialize all test variants
    const initialVariants = {};
    Object.keys(AB_TESTS).forEach(testName => {
      initialVariants[testName] = getVariant(testName);
    });
    setVariants(initialVariants);
  }, []);
  
  const getTestVariant = useCallback((testName) => {
    const variantId = variants[testName];
    const test = AB_TESTS[testName];
    if (!test || !variantId) return null;
    return test.variants.find(v => v.id === variantId);
  }, [variants]);
  
  const getButtonText = useCallback((testName, language = "en") => {
    const variant = getTestVariant(testName);
    if (!variant || !AB_TESTS[testName]?.texts) return "";
    const textKey = variant.text;
    return AB_TESTS[testName].texts[textKey]?.[language] || AB_TESTS[testName].texts[textKey]?.en || "";
  }, [getTestVariant]);
  
  const trackEvent = useCallback((testName, eventType, metadata = {}) => {
    const variantId = variants[testName];
    if (variantId) {
      trackABEvent(testName, variantId, eventType, metadata);
    }
  }, [variants]);
  
  return (
    <ABTestContext.Provider value={{ 
      variants, 
      getTestVariant, 
      getButtonText,
      trackEvent,
      AB_TESTS 
    }}>
      {children}
    </ABTestContext.Provider>
  );
};

export const useABTest = () => {
  const context = useContext(ABTestContext);
  if (!context) {
    throw new Error("useABTest must be used within ABTestProvider");
  }
  return context;
};

export default ABTestProvider;
